import * as THREE from 'three';
import { Vec3 } from '@/core/math/Vec3';
import { Quat } from '@/core/math/Quat';
import { ChunkRecord } from '@/planet/PlanetSurface';
import { RENDER_LAYER } from '../LayeredRenderer';
import { ALL_SPECIES, getSpecies, isGroundCoverType } from '@/planet/VegetationDef';
import {
  cloneBillboardMaterialForFade,
  cloneSolidMaterialForFade,
  getBillboardQuad,
  getBillboardMaterial,
  getSolidGeometry,
  getSolidMaterial,
  liveBillboardMaterials,
  speciesFootprint,
} from './VegetationGeometry';

export type VegetationTier = 'high' | 'medium' | 'billboard' | 'hidden';
type Tier = VegetationTier;
type SolidTier = 'high' | 'medium';

interface MeshEntry {
  mesh: THREE.InstancedMesh;
  speciesId: string;
  fade: { value: number };
}

/**
 * A dithered-alpha crossfade between the tier a chunk was showing and the
 * tier it's moving to. `fromMeshes` are the *same* mesh objects that were
 * `state.meshes` a moment ago — only their `.material` is swapped to an
 * owned fade clone, so no instance data is recomputed to start a fade.
 * `toMeshes` are freshly built (their transforms are new work regardless of
 * fading, since a change of tier always means different geometry/instance
 * count). Both fade in lockstep from the same `startTime`, one 1->0 and the
 * other 0->1, so the dithered discard in each material partitions screen
 * pixels between them without double-coverage — see `DITHER_FADE_GLSL`'s
 * doc comment in VegetationGeometry.ts.
 */
interface Transition {
  toTier: Tier;
  toMeshes: MeshEntry[];
  fromMeshes: MeshEntry[];
  startTime: number;
}

interface ChunkVegState {
  record: ChunkRecord;
  group: THREE.Group;
  centre: [number, number, number];
  boundingRadius: number;
  count: number;
  positions: Float32Array;
  normals: Float32Array;
  speciesIndex: Uint16Array;
  scale: Float32Array;
  rotationY: Float32Array;
  colorJitter: Float32Array;
  /** Settled tier; the target tier while `transition` is active. */
  tier: Tier;
  /** Steady-state meshes for `tier`, on the shared (non-cloned) materials. Empty during `transition` — see `Transition`. */
  meshes: MeshEntry[];
  transition: Transition | null;
  /**
   * Whether `PlanetSurface`'s LOD tree currently wants this exact chunk
   * drawn, as opposed to a coarser parent it merged into while this one
   * still lingers resident. A finest-level chunk (the only kind that ever
   * carries vegetation) never subdivides further, but it very much can stop
   * being the level the tree is showing once the camera moves away — its
   * own terrain mesh gets hidden the same way (`TerrainRenderer.setVisible`)
   * while an old build stays cached, and without this check its vegetation
   * would keep rendering, floating over whatever coarser ground actually
   * replaced it.
   */
  streamVisible: boolean;
  /** Last conservative camera-to-geometry distance, used when stream visibility changes between updates. */
  drawDistance: number;
}

/** Distance bands, metres, nearest first. Each has a wider 'stay' threshold than its 'enter' one — the same hysteresis-gap shape as `PlanetSurface`'s split/merge and the body promote/demote distances in `Game.ts`. */
const HIGH_ENTER = 85;
const HIGH_EXIT = 110;
const MEDIUM_ENTER = 320;
const MEDIUM_EXIT = 390;
const BILLBOARD_DISTANCE_MULTIPLIER = 1.75;
/** The billboard band's hysteresis exit, and the far end of its screen-door fade, as multiples of the cutoff. */
const BILLBOARD_EXIT_MULTIPLIER = 1.15;
const BILLBOARD_FADE_NEAR_MULTIPLIER = 0.82;
/**
 * Fraction of `PlanetSurface.vegetationRange` the fade is allowed to reach.
 * The range itself is exact, and a fade that finishes exactly on it still ends
 * on the same frame the chunk disappears — leave a margin so the last cards
 * are already invisible by then.
 */
const GENERATION_RANGE_SAFETY = 0.95;

/**
 * Largest scatter distance whose billboard fade still completes inside
 * `generationRange` — the distance beyond which `PlanetSurface` simply does
 * not put vegetation on a chunk at all (see its `vegetationRange`).
 *
 * A quality tier's `scatterDistance` is a *budget*, not a promise the terrain
 * streamer can keep: at high quality the fade band used to begin several
 * hundred metres past the point where the coarsest vegetated LOD level had
 * already merged away, so the cards were still at full opacity when the chunk
 * carrying them vanished. That is the hard, chunk-aligned forest border —
 * fixing it in the fade alone is impossible, because there is nothing left to
 * fade.
 */
export function vegetationScatterDistanceFor(scatterDistance: number, generationRange: number): number {
  if (!(generationRange > 0)) return scatterDistance;
  const maxScatter =
    (generationRange * GENERATION_RANGE_SAFETY) / (BILLBOARD_DISTANCE_MULTIPLIER * BILLBOARD_EXIT_MULTIPLIER);
  return Math.max(1, Math.min(scatterDistance, maxScatter));
}

/** Distant cards extend the profile range without increasing chunk payloads or solid geometry. */
export function vegetationBillboardDistance(scatterDistance: number): number {
  return Math.max(1, scatterDistance) * BILLBOARD_DISTANCE_MULTIPLIER;
}

/** Broad screen-door fade interval ending at the billboard hysteresis exit. */
export function vegetationBillboardFadeRange(scatterDistance: number): { near: number; far: number } {
  const cutoff = vegetationBillboardDistance(scatterDistance);
  return { near: cutoff * BILLBOARD_FADE_NEAR_MULTIPLIER, far: cutoff * BILLBOARD_EXIT_MULTIPLIER };
}

/** Conservative gap from the camera to resident chunk geometry. */
export function vegetationDrawDistance(
  centreDistance: number,
  boundingRadius: number,
  terrainAltitude = 0,
): number {
  const horizontalGap = Math.max(0, centreDistance - Math.max(0, boundingRadius));
  return Math.hypot(horizontalGap, Math.max(0, terrainAltitude));
}

/** How long a dithered crossfade between two tiers takes, seconds. Tied to sim time like every other animated render layer, so it freezes with the rest of the world while paused or (implicitly) scales with time warp. */
const TRANSITION_SECONDS = 0.7;

/**
 * Renders one body's vegetation, one small chunk-scoped group per chunk that
 * carries instance data, positioned every frame exactly like
 * `TerrainRenderer`'s chunk meshes (camera-relative offset plus the
 * body-fixed-to-inertial rotation). Instance transforms are computed once,
 * when a tier is (re)built — there is no per-instance per-frame work, only
 * the one group reposition per chunk, a per-chunk distance check against
 * `record.distance` (which `PlanetSurface` already recomputes every frame
 * for its own LOD decision), and — while a chunk is mid-transition — two
 * scalar uniform writes.
 */
export class VegetationLayer {
  readonly root = new THREE.Group();
  private chunks = new Map<string, ChunkVegState>();
  private scatterDistance: number;
  private currentTime = 0;

  private readonly tmpPosition = new THREE.Vector3();
  private readonly tmpNormal = new THREE.Vector3();
  private readonly tmpUp = new THREE.Vector3(0, 1, 0);
  private readonly tmpLeanAxis = new THREE.Vector3(1, 0, 0);
  private readonly tmpAlign = new THREE.Quaternion();
  private readonly tmpSpin = new THREE.Quaternion();
  private readonly tmpTilt = new THREE.Quaternion();
  private readonly tmpScale = new THREE.Vector3();
  private readonly tmpMatrix = new THREE.Matrix4();
  private readonly tmpColor = new THREE.Color();
  private readonly tmpDelta = new Vec3();

  constructor(scatterDistance: number) {
    this.root.frustumCulled = false;
    this.scatterDistance = Math.max(1, scatterDistance);
  }

  setScatterDistance(distance: number): void {
    this.scatterDistance = Math.max(1, distance);
  }

  onChunkReady(record: ChunkRecord): void {
    const veg = record.data?.vegetation;
    this.removeInternal(record.key);
    if (!veg || veg.count === 0 || !record.data) return;

    const group = new THREE.Group();
    group.frustumCulled = false;
    const state: ChunkVegState = {
      record,
      group,
      centre: record.data.centre,
      boundingRadius: record.data.boundingRadius,
      count: veg.count,
      positions: veg.positions,
      normals: veg.normals,
      speciesIndex: veg.speciesIndex,
      scale: veg.scale,
      rotationY: veg.rotationY,
      colorJitter: veg.colorJitter,
      tier: 'hidden',
      meshes: [],
      transition: null,
      streamVisible: true,
      drawDistance: record.distance,
    };
    this.chunks.set(record.key, state);
    this.root.add(group);
    // Routes a brand-new chunk through the same crossfade as any other tier
    // change (fading in from nothing), rather than special-casing an instant
    // appearance — one code path for both.
    this.beginTransition(state, this.pickTier(record.distance, 'hidden'));
  }

  onChunkRemoved(key: string): void {
    this.removeInternal(key);
  }

  /**
   * Repositions every live chunk group, advances any in-flight crossfade,
   * and starts a new one where the distance crossed a tier threshold.
   *
   * `TerrainRenderer` gets group positioning right by splitting the work
   * across two objects: a single parent group carries `fixedToInertial` as
   * its rotation, and each chunk mesh only ever sets an *unrotated*
   * `centre - camera` translation as a child of that parent — so Three's own
   * `parent.matrixWorld * child.matrix` multiplication is what applies the
   * rotation to the translation. A lone `THREE.Group` has no such parent to
   * do that for it: composing position and quaternion on the *same* object
   * applies rotation to its local geometry only and leaves the translation
   * untouched, i.e. `worldPoint = (centre - camera) + R * localPoint` instead
   * of the correct `R * (localPoint + centre - camera)`. Those agree only
   * when `R` is the identity, so the error grows with the planet's rotation
   * — vegetation drifting away from the terrain beneath it as the body spins
   * relative to whatever it was at chunk-build time. Rotating the
   * translation here before assigning it is what makes a lone group behave
   * like `TerrainRenderer`'s parent+child pair.
   */
  update(cameraPositionFixed: Vec3, fixedToInertial: Quat, t: number, terrainAltitude = 0): void {
    this.currentTime = t;
    for (const state of this.chunks.values()) {
      const c = state.centre;
      this.tmpDelta.set(c[0] - cameraPositionFixed.x, c[1] - cameraPositionFixed.y, c[2] - cameraPositionFixed.z);
      fixedToInertial.rotate(this.tmpDelta, this.tmpDelta);
      state.group.quaternion.set(fixedToInertial.x, fixedToInertial.y, fixedToInertial.z, fixedToInertial.w);
      state.group.position.set(this.tmpDelta.x, this.tmpDelta.y, this.tmpDelta.z);
      // PlanetSurface's LOD distance samples the chunk at its maximum terrain
      // radius. Near the ground that can report a camera hundreds of metres
      // away merely because the same chunk contains a higher hill. The
      // resident geometry's bounding sphere gives the conservative lateral
      // gap needed here, while the real terrain altitude preserves the
      // vertical component (so 9 m is high detail and 119 m is not).
      const drawDistance = vegetationDrawDistance(this.tmpDelta.length(), state.boundingRadius, terrainAltitude);
      state.drawDistance = drawDistance;

      if (state.transition) {
        this.advanceTransition(state, t);
      } else if (state.streamVisible) {
        const nextTier = this.pickTier(drawDistance, state.tier);
        if (nextTier !== state.tier) this.beginTransition(state, nextTier);
      } else if (state.tier !== 'hidden') {
        this.beginTransition(state, 'hidden');
      }
    }
  }

  /**
   * Shows only the chunks `PlanetSurface`'s LOD tree currently wants drawn —
   * the same `surface.visible` set `TerrainRenderer.setVisible` is called
   * with, and for the same reason: a chunk can stay resident (and touched,
   * so never evicted) well after its parent has merged and taken over
   * showing that ground instead.
   */
  setVisible(keys: ReadonlySet<string>): void {
    for (const [key, state] of this.chunks) {
      const visible = keys.has(key);
      if (visible === state.streamVisible) continue;
      state.streamVisible = visible;
      if (visible) {
        state.group.visible = true;
        this.beginTransition(state, this.pickTier(state.drawDistance, 'hidden'));
      } else if (state.tier !== 'hidden' || state.transition) {
        // Keep the group rendered until its outgoing tier reaches zero. This
        // also covers a fine terrain chunk being replaced by its coarser
        // parent, which previously bypassed the LOD fade entirely.
        this.beginTransition(state, 'hidden');
      }
    }
  }

  /**
   * Only the billboard tier needs a manual lighting uniform; the solid
   * tiers use `MeshStandardMaterial` and are lit by the scene's own lights.
   * Shared per-silhouette materials and active transition clones are both
   * updated so newly created cards receive the correct distance range on
   * their first frame. The clone scan is bounded to the handful of chunks
   * currently crossfading.
   */
  setLighting(sunColor: THREE.Color, intensity: number): void {
    const fadeRange = vegetationBillboardFadeRange(this.scatterDistance);
    const updateBillboard = (material: THREE.ShaderMaterial): void => {
      (material.uniforms.uSunColor.value as THREE.Color).copy(sunColor);
      material.uniforms.uSunIntensity.value = intensity;
      material.uniforms.uFadeNear.value = fadeRange.near;
      material.uniforms.uFadeFar.value = fadeRange.far;
    };
    for (const material of liveBillboardMaterials()) updateBillboard(material);
    for (const state of this.chunks.values()) {
      if (!state.transition) continue;
      for (const entry of [...state.transition.fromMeshes, ...state.transition.toMeshes]) {
        const material = entry.mesh.material;
        if (material instanceof THREE.ShaderMaterial) updateBillboard(material);
      }
    }
  }

  dispose(): void {
    for (const key of Array.from(this.chunks.keys())) this.removeInternal(key);
  }

  private pickTier(distance: number, current: Tier): Tier {
    const cutoff = vegetationBillboardDistance(this.scatterDistance);
    const bands: { tier: Tier; enter: number; exit: number }[] = [
      { tier: 'high', enter: HIGH_ENTER, exit: HIGH_EXIT },
      { tier: 'medium', enter: MEDIUM_ENTER, exit: MEDIUM_EXIT },
      { tier: 'billboard', enter: cutoff, exit: cutoff * BILLBOARD_EXIT_MULTIPLIER },
    ];
    for (const band of bands) {
      const threshold = current === band.tier ? band.exit : band.enter;
      if (distance < threshold) return band.tier;
    }
    return 'hidden';
  }

  private cloneForFade(tier: SolidTier | 'billboard', speciesId: string, start: number): { material: THREE.Material; fade: { value: number } } {
    if (tier === 'billboard') return cloneBillboardMaterialForFade(getSpecies(speciesId), start);
    return cloneSolidMaterialForFade(start);
  }

  /** Builds fresh instanced meshes for `tier`, each on its own fade-clone material starting at `fadeStart`. */
  private buildTierMeshes(state: ChunkVegState, tier: SolidTier | 'billboard', fadeStart: number): MeshEntry[] {
    const bySpecies = new Map<string, number[]>();
    for (let i = 0; i < state.count; i++) {
      const species = ALL_SPECIES[state.speciesIndex[i]];
      if (!species) continue;
      // Sub-metre cover contributes only in the near tier.  Carrying it into
      // medium/billboard tiers produced sub-pixel triangles and alpha overdraw
      // without changing the forest silhouette.
      if ((isGroundCoverType(species.type) || species.type === 'bush') && tier !== 'high') continue;
      let indices = bySpecies.get(species.id);
      if (!indices) {
        indices = [];
        bySpecies.set(species.id, indices);
      }
      indices.push(i);
    }

    const entries: MeshEntry[] = [];
    for (const [speciesId, indices] of bySpecies) {
      const species = getSpecies(speciesId);
      const geometry = tier === 'billboard' ? getBillboardQuad() : getSolidGeometry(species, tier);
      const { material, fade } = this.cloneForFade(tier, speciesId, fadeStart);
      const footprint = speciesFootprint(species);

      const mesh = new THREE.InstancedMesh(geometry, material, indices.length);
      mesh.frustumCulled = false;
      // Only the near tiers cast into the cascades — see
      // shadowDepthMaterial's comment in LayeredRenderer: a plain override
      // material doesn't reproduce the billboard shader's camera-facing
      // vertex trick, so a billboard caster would draw as a static,
      // wrongly-oriented quad shadow rather than being skipped like this.
      if (tier !== 'billboard') mesh.layers.enable(RENDER_LAYER.terrainShadowCaster);

      for (let n = 0; n < indices.length; n++) {
        const i = indices[n];
        const o = i * 3;
        this.tmpPosition.set(state.positions[o], state.positions[o + 1], state.positions[o + 2]);
        this.tmpNormal.set(state.normals[o], state.normals[o + 1], state.normals[o + 2]);

        this.tmpAlign.setFromUnitVectors(this.tmpUp, this.tmpNormal);
        this.tmpSpin.setFromAxisAngle(this.tmpUp, state.rotationY[i]);
        this.tmpAlign.multiply(this.tmpSpin);

        const averageHeight = (species.heightRange[0] + species.heightRange[1]) * 0.5;
        const averageRadius = (species.radiusRange[0] + species.radiusRange[1]) * 0.5;
        const heightScale =
          (species.heightRange[0] + (species.heightRange[1] - species.heightRange[0]) * state.scale[i]) /
          Math.max(0.001, averageHeight);
        const radiusRoll = (state.scale[i] * 0.61803398875 + state.colorJitter[i] * 0.38196601125) % 1;
        const radiusScale =
          (species.radiusRange[0] + (species.radiusRange[1] - species.radiusRange[0]) * radiusRoll) /
          Math.max(0.001, averageRadius);
        const widthVariation = 0.9 + 0.2 * ((Math.sin(state.rotationY[i] * 3.17) + 1) * 0.5);
        const depthVariation = 0.9 + 0.2 * ((Math.cos(state.rotationY[i] * 2.73) + 1) * 0.5);

        if (tier === 'billboard') {
          this.tmpScale.set(footprint.width * radiusScale * widthVariation, footprint.height * heightScale, 1);
        } else {
          const maxLean = species.type === 'tree' ? 0.07 : isGroundCoverType(species.type) ? 0.14 : 0.09;
          const lean = (state.colorJitter[i] - 0.5) * 2 * maxLean;
          this.tmpTilt.setFromAxisAngle(this.tmpLeanAxis, lean);
          this.tmpAlign.multiply(this.tmpTilt);
          this.tmpScale.set(radiusScale * widthVariation, heightScale, radiusScale * depthVariation);
        }

        this.tmpMatrix.compose(this.tmpPosition, this.tmpAlign, this.tmpScale);
        mesh.setMatrixAt(n, this.tmpMatrix);

        const t = Math.min(1, Math.max(0, state.colorJitter[i] * 1.12));
        const brightness = 0.9 + 0.18 * ((Math.sin(state.rotationY[i] * 4.11) + 1) * 0.5);
        this.tmpColor.setRGB(
          (species.color[0] + (species.detailColor[0] - species.color[0]) * t) * brightness,
          (species.color[1] + (species.detailColor[1] - species.color[1]) * t) * brightness,
          (species.color[2] + (species.detailColor[2] - species.color[2]) * t) * brightness,
        );
        mesh.setColorAt(n, this.tmpColor);
      }

      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      entries.push({ mesh, speciesId, fade });
    }
    return entries;
  }

  /** Starts a crossfade from `state`'s current tier to `toTier`. Snaps any transition already in flight to completion first, so at most one is ever active per chunk. */
  private beginTransition(state: ChunkVegState, toTier: Tier): void {
    if (toTier === state.tier && !state.transition) return;
    if (state.transition) this.finishTransition(state);

    const fromTier = state.tier;
    const fromMeshes: MeshEntry[] = [];
    if (fromTier !== 'hidden') {
      for (const entry of state.meshes) {
        // Reuse the existing mesh (its instance transforms don't change) and
        // swap only its material to an owned clone, starting fully visible
        // and fading toward 0 — no need to recompute anything to begin
        // fading the outgoing tier out.
        const { material, fade } = this.cloneForFade(fromTier as SolidTier | 'billboard', entry.speciesId, 1);
        entry.mesh.material = material;
        fromMeshes.push({ mesh: entry.mesh, speciesId: entry.speciesId, fade });
      }
    }
    state.meshes = [];

    const toMeshes = toTier === 'hidden' ? [] : this.buildTierMeshes(state, toTier as SolidTier | 'billboard', 0);
    for (const entry of toMeshes) state.group.add(entry.mesh);

    state.group.visible = state.streamVisible || toTier === 'hidden';
    state.transition = { toTier, toMeshes, fromMeshes, startTime: this.currentTime };
  }

  private advanceTransition(state: ChunkVegState, t: number): void {
    const transition = state.transition;
    if (!transition) return;
    const progress = Math.min(1, (t - transition.startTime) / TRANSITION_SECONDS);
    for (const entry of transition.toMeshes) entry.fade.value = progress;
    for (const entry of transition.fromMeshes) entry.fade.value = 1 - progress;
    if (progress >= 1) this.finishTransition(state);
  }

  /** Snaps a transition to completion: disposes the outgoing tier, and promotes the incoming tier to steady state on the shared (non-cloned) material. */
  private finishTransition(state: ChunkVegState): void {
    const transition = state.transition;
    if (!transition) return;

    for (const entry of transition.fromMeshes) {
      state.group.remove(entry.mesh);
      (entry.mesh.material as THREE.Material).dispose();
      entry.mesh.dispose();
    }

    for (const entry of transition.toMeshes) {
      (entry.mesh.material as THREE.Material).dispose();
      entry.mesh.material =
        transition.toTier === 'billboard' ? getBillboardMaterial(getSpecies(entry.speciesId)) : getSolidMaterial();
    }

    state.tier = transition.toTier;
    state.meshes = transition.toMeshes;
    state.transition = null;
    state.group.visible = state.streamVisible && state.tier !== 'hidden';
  }

  /** Full teardown for a chunk being evicted — disposes steady meshes and, if applicable, an in-flight transition's meshes on both sides. */
  private disposeAll(state: ChunkVegState): void {
    for (const entry of state.meshes) {
      state.group.remove(entry.mesh);
      // The shared singleton material is never touched here — only the
      // per-chunk InstancedMesh wrapper and its own instance buffers belong
      // to this chunk.
      entry.mesh.dispose();
    }
    state.meshes = [];

    if (state.transition) {
      for (const entry of [...state.transition.fromMeshes, ...state.transition.toMeshes]) {
        state.group.remove(entry.mesh);
        (entry.mesh.material as THREE.Material).dispose();
        entry.mesh.dispose();
      }
      state.transition = null;
    }
  }

  private removeInternal(key: string): void {
    const state = this.chunks.get(key);
    if (!state) return;
    this.disposeAll(state);
    this.root.remove(state.group);
    this.chunks.delete(key);
  }
}
